/**
 * The Integrations page — a card gallery by category (FR-A4, C1; C11 FR-A8
 * §10; [dukaan](../../../../../docs/legacy/dukaan.md) §1).
 *
 * Dukaan's plugin marketplace is 87 cards in 8 categories with "Install
 * plugin" on each. **What carries over is the gallery, not the verb.** Nothing
 * is installed into ProDesk: core holds the credential, core enables the
 * integration, and this screen lists and reads (CLAUDE.md §7). The action on a
 * card is a link to the platform console and never a switch here.
 *
 * **w12a (T-1.20)** — the page wears the settings shell and a card on the hub;
 * each card has an icon, a state badge (the kit's `Badge`, no colours of its
 * own) and **one** action. Every card still carries one line on what changes
 * without it, because a badge with no consequence is decoration.
 *
 * **The states are real per-org answers from 2026-09-22**, read from
 * `GET /internal/providers/state?org&family` for five families through
 * `app/platform/gateway.py` (PROVIDER_CONNECTORS.md §3); the vocabulary on the
 * cards is the contract's, so this tab and the console's Providers tab agree
 * (contract §6).
 *
 * **w17a (T-5.54, ADR-0035 §3)** — seven families: *Google sign-in* and
 * *Microsoft sign-in* (`identity_google`, `identity_microsoft`; platform
 * ADR-0109, waves.md I-3) join under *Client sign-in*, read and linked to the
 * console exactly like the other five. Connected, the firm's clients meet the
 * firm's own app; not, FiveOX's. ProDesk holds nothing.
 */

import { Alert, Badge, type BadgeTone, EmptyState, Skeleton, buttonClass } from '@fiveox/ui'
import type { LucideIcon } from 'lucide-react'
import {
  CreditCard,
  ExternalLink,
  Inbox,
  KeyRound,
  LogIn,
  Mail,
  MessageCircle,
  MessageSquare,
  Plug,
  Puzzle,
  Smartphone,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import {
  type IntegrationCard,
  type IntegrationsPayload,
  attentionBanner,
  canEnableInCore,
  cardLine,
  firm,
  groupCards,
  stateExplanation,
  stateLabel,
  stateTone,
} from '../../lib/api/firm'
import { CONSOLE_URL } from '../../lib/config'
import { SettingsPage } from './settings/SettingsPage'

const CATEGORY_ICONS: Record<string, ReactNode> = {
  payments: <CreditCard size={16} aria-hidden />,
  channels: <MessageSquare size={16} aria-hidden />,
  mailbox: <Inbox size={16} aria-hidden />,
  sign_in: <LogIn size={16} aria-hidden />,
  handoff: <Puzzle size={16} aria-hidden />,
}

/** One icon per provider family (T-1.20). */
export const CARD_ICONS: Record<string, LucideIcon> = {
  payment_gateway: CreditCard,
  email: Mail,
  whatsapp: MessageCircle,
  sms: Smartphone,
  email_inbound: Inbox,
  identity_google: KeyRound,
  identity_microsoft: KeyRound,
}

/** The two families a firm's clients sign in through (T-5.54). */
export const IDENTITY_FAMILIES: readonly string[] = ['identity_google', 'identity_microsoft']

/**
 * The attention banner, naming a broken sign-in app for what it does.
 *
 * `attentionBanner` speaks for the payment gateway and the channels — *"your
 * messages still go out, but not from your firm"* — which is not what a
 * Google or Microsoft app that stopped verifying does: its clients meet
 * FiveOX's app instead. So the identity cards are said here and the rest is
 * left to the shared sentence.
 */
export function integrationsAttention(cards: IntegrationCard[]): string | null {
  const identity = cards.filter(
    (card) => card.state === 'attention' && IDENTITY_FAMILIES.includes(card.code),
  )
  const rest = attentionBanner(cards.filter((card) => !IDENTITY_FAMILIES.includes(card.code)))
  const lines = rest ? [rest] : []
  if (identity.length > 0) {
    lines.push(
      `Your ${identity.map((card) => card.name).join(' and ')} app isn’t verifying. Until it does, clients who use it meet FiveOX’s app instead of yours.`,
    )
  }
  return lines.length > 0 ? lines.join(' ') : null
}

const TONE_BADGE: Record<ReturnType<typeof stateTone>, BadgeTone> = {
  good: 'success',
  neutral: 'neutral',
  muted: 'neutral',
  warn: 'warning',
}

/** The one action a card offers — a link out, or a plain sentence when there
 *  is nothing a firm can do from here. */
export function cardAction(card: IntegrationCard): { label: string; href: string | null } {
  if (canEnableInCore(card.state)) {
    return {
      label: card.state === 'attention' ? 'Fix in the console' : 'Connect in the console',
      href: `${CONSOLE_URL}/integrations?tab=providers`,
    }
  }
  if (card.state === 'active') {
    return { label: 'Manage in the console', href: `${CONSOLE_URL}/integrations?tab=providers` }
  }
  return {
    label: card.state === 'not_offered' ? 'Changes with your plan, not a switch' : 'Nothing to do here yet',
    href: null,
  }
}

function IntegrationTile({ card }: { card: IntegrationCard }) {
  const explanation = stateExplanation(card)
  const Icon = CARD_ICONS[card.code] ?? Plug
  const action = cardAction(card)
  return (
    <article
      data-integration={card.code}
      className="border-border flex h-full flex-col gap-4 rounded-2xl border bg-white p-5 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
            card.state === 'active' ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
          }`}
        >
          <Icon size={20} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-foreground text-base font-semibold">{card.name}</h3>
            <Badge tone={TONE_BADGE[stateTone(card.state)]} size="sm">
              {stateLabel(card.state)}
            </Badge>
          </div>
          {card.state === 'active' && card.provider && (
            <p className="text-muted-foreground mt-0.5 text-xs">
              Through {card.provider}
              {card.verified_at
                ? ` · verified ${new Date(card.verified_at).toLocaleDateString()}`
                : ''}
            </p>
          )}
        </div>
      </div>

      <p className="text-muted-foreground flex-1 text-sm">{cardLine(card)}</p>

      {explanation && <Alert tone="warning">{explanation}</Alert>}

      {card.state === 'active' && card.since && (
        // Ours, not core's: when the answer *to us* last changed.
        <p className="text-muted-foreground text-xs">
          Active here since {new Date(card.since).toLocaleDateString()}.
        </p>
      )}

      <div className="border-border border-t pt-3">
        {action.href ? (
          <a
            href={action.href}
            target="_blank"
            rel="noreferrer"
            className={buttonClass({
              variant: card.state === 'active' ? 'ghost' : 'outline',
              size: 'sm',
            })}
          >
            {action.label} <ExternalLink size={14} aria-hidden />
          </a>
        ) : (
          <span className="text-muted-foreground text-sm">{action.label}</span>
        )}
      </div>
    </article>
  )
}

export function Integrations() {
  const [payload, setPayload] = useState<IntegrationsPayload | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    firm
      .integrations()
      .then((body) => {
        if (!cancelled) setPayload(body.data)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Integrations could not be loaded.')
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <SettingsPage
      title="Integrations"
      description="What your organisation has switched on in the platform, and what ProDesk does with it. Nothing is installed into ProDesk — the platform holds the keys and does the sending."
    >
      {error && <Alert tone="error">{error}</Alert>}

      {!payload && !error && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full rounded-2xl" />
          ))}
        </div>
      )}

      {payload?.any_attention && (
        // **First, and above the can't-confirm banner.** This one is about
        // something already going wrong for the firm's clients.
        <Alert tone="warning">
          {integrationsAttention(payload.cards) ??
            'Something you’ve connected isn’t verifying. Until it does, ProDesk behaves as if it weren’t connected at all.'}
        </Alert>
      )}

      {payload?.any_unavailable && (
        // One banner, not five copies of the same fact on five cards.
        <Alert tone="warning">
          We couldn’t reach the platform’s integration service, so some states below could not be
          confirmed. Nothing is treated as active until we can read it.
        </Alert>
      )}

      {payload && (
        <div className="space-y-8">
          {groupCards(payload.categories, payload.cards).map(({ category, cards }) => (
            <section key={category.code} aria-labelledby={`integrations-${category.code}`}>
              <div className="mb-3">
                <h2
                  id={`integrations-${category.code}`}
                  className="text-muted-foreground flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
                >
                  {CATEGORY_ICONS[category.code]}
                  {category.label}
                </h2>
                <p className="text-muted-foreground mt-1 max-w-3xl text-sm">{category.note}</p>
              </div>

              {cards.length === 0 ? (
                <EmptyState variant="outline" title="Nothing here yet" />
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {cards.map((card) => (
                    <IntegrationTile key={card.code} card={card} />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </SettingsPage>
  )
}
